import { supabase } from 'src/lib/supabaseClient';

// Types for email service
export interface EmailTemplate {
  channel: 'booking' | 'onboarding' | 'notifications' | 'marketing';
  template: string;
  to: string | string[];
  cc?: string | string[];
  data?: Record<string, any>;
  subject?: string;
  from?: string;
  replyTo?: string;
  bcc?: string | string[];
}

export interface RawEmail {
  channel: 'booking' | 'onboarding' | 'notifications' | 'marketing';
  to: string | string[];
  cc?: string | string[];
  subject: string;
  rawHtml: string;
  text?: string;
  from?: string;
  replyTo?: string;
  bcc?: string | string[];
}

export interface EmailResponse {
  success: boolean;
  message: string;
  emailId?: string;
  error?: string;
  details?: any;
}

// Available email templates
export const EMAIL_TEMPLATES = {
  // Booking templates
  DEMO_BOOKING_CONFIRMATION: 'demo_booking_confirmation',
  DEMO_REMINDER: 'demo_reminder',
  DEMO_CANCELLATION: 'demo_cancellation',
  MEETING_DETAILS: 'meeting_details',
  
  // Onboarding templates
  WELCOME_STUDENT: 'welcome_student',
  WELCOME_TUTOR: 'welcome_tutor',
  EMAIL_VERIFICATION: 'email_verification',
  
  // Notification templates
  SESSION_REMINDER: 'session_reminder',
  PAYMENT_CONFIRMATION: 'payment_confirmation',
  PROFILE_UPDATE: 'profile_update',
} as const;

export const DEMO_BOOKING_CC_RECIPIENTS = [
  'support@rflearn.com',
  'tech@rflearn.com'
];

class EmailService {
  private readonly functionUrl: string;

  constructor() {
    // Get the Supabase function URL
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    this.functionUrl = `${supabaseUrl}/functions/v1/send-email`;
  }

  /**
   * Send an email using a Resend template
   */
  async sendTemplateEmail(emailData: EmailTemplate): Promise<EmailResponse> {
    try {
      console.log(`📧 Sending template email: ${emailData.template} to ${emailData.to}`);

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: emailData
      });

      if (error) {
        console.error('❌ Email service error:', error);
        return {
          success: false,
          message: 'Failed to send email',
          error: error.message,
          details: error
        };
      }

      console.log('✅ Email sent successfully:', data);
      return data;

    } catch (error) {
      console.error('❌ Email service error:', error);
      return {
        success: false,
        message: 'Failed to send email',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Send an email with raw HTML content
   */
  async sendRawEmail(emailData: RawEmail): Promise<EmailResponse> {
    try {
      console.log(`📧 Sending raw HTML email to ${emailData.to}`);

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: emailData
      });

      if (error) {
        console.error('❌ Email service error:', error);
        return {
          success: false,
          message: 'Failed to send email',
          error: error.message,
          details: error
        };
      }

      console.log('✅ Email sent successfully:', data);
      return data;

    } catch (error) {
      console.error('❌ Email service error:', error);
      return {
        success: false,
        message: 'Failed to send email',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Send demo booking confirmation email
   */
  async sendDemoBookingConfirmation(
    recipientEmail: string,
    bookingData: {
      studentName: string;
      tutorName: string;
      tutorEmail: string;
      date: string;
      time: string;
      duration: string;
      subject: string;
      meetingUrl?: string;
      timezone: string;
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'booking',
      template: EMAIL_TEMPLATES.DEMO_BOOKING_CONFIRMATION,
      to: recipientEmail,
      cc: DEMO_BOOKING_CC_RECIPIENTS,
      bcc: bookingData.tutorEmail,
      subject: `Demo Session Confirmed - ${bookingData.date} at ${bookingData.time}`,
      data: {
        student_name: bookingData.studentName,
        tutor_name: bookingData.tutorName,
        tutor_email: bookingData.tutorEmail,
        session_date: bookingData.date,
        session_time: bookingData.time,
        session_duration: bookingData.duration,
        subject: bookingData.subject,
        meeting_url: bookingData.meetingUrl,
        timezone: bookingData.timezone,
        support_email: 'support@rflearn.com',
        company_name: 'rfLearn'
      }
    });
  }

  /**
   * Send meeting details email
   */
  async sendMeetingDetails(
    recipientEmail: string,
    meetingData: {
      studentName: string;
      tutorName: string;
      tutorEmail: string;
      date: string;
      time: string;
      meetingUrl: string;
      meetingId: string;
      timezone: string;
      icsAttachment?: string;
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'booking',
      template: EMAIL_TEMPLATES.MEETING_DETAILS,
      to: recipientEmail,
      bcc: meetingData.tutorEmail,
      subject: `Meeting Details - ${meetingData.date} at ${meetingData.time}`,
      data: {
        student_name: meetingData.studentName,
        tutor_name: meetingData.tutorName,
        tutor_email: meetingData.tutorEmail,
        session_date: meetingData.date,
        session_time: meetingData.time,
        meeting_url: meetingData.meetingUrl,
        meeting_id: meetingData.meetingId,
        timezone: meetingData.timezone,
        ics_attachment: meetingData.icsAttachment,
        support_email: 'support@rflearn.com'
      }
    });
  }

  /**
   * Send demo reminder email
   */
  async sendDemoReminder(
    recipientEmail: string,
    reminderData: {
      studentName: string;
      tutorName: string;
      tutorEmail: string;
      date: string;
      time: string;
      meetingUrl: string;
      timezone: string;
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'booking',
      template: EMAIL_TEMPLATES.DEMO_REMINDER,
      to: recipientEmail,
      bcc: reminderData.tutorEmail,
      subject: `Reminder: Demo Session Tomorrow - ${reminderData.date} at ${reminderData.time}`,
      data: {
        student_name: reminderData.studentName,
        tutor_name: reminderData.tutorName,
        tutor_email: reminderData.tutorEmail,
        session_date: reminderData.date,
        session_time: reminderData.time,
        meeting_url: reminderData.meetingUrl,
        timezone: reminderData.timezone,
        support_email: 'support@rflearn.com'
      }
    });
  }

  /**
   * Send welcome email to new students
   */
  async sendWelcomeStudent(
    recipientEmail: string,
    studentData: {
      firstName: string;
      lastName: string;
      loginUrl: string;
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'onboarding',
      template: EMAIL_TEMPLATES.WELCOME_STUDENT,
      to: recipientEmail,
      subject: `Welcome to rfLearn, ${studentData.firstName}!`,
      data: {
        first_name: studentData.firstName,
        last_name: studentData.lastName,
        login_url: studentData.loginUrl,
        support_email: 'support@rflearn.com',
        company_name: 'rfLearn'
      }
    });
  }

  /**
   * Send session reminder email
   */
  async sendSessionReminder(
    recipientEmail: string,
    reminderData: {
      studentName: string;
      tutorName: string;
      tutorEmail: string;
      sessionDate: string;
      sessionTime: string;
      subject: string;
      meetingUrl: string;
      sessionType: string;
      minutesUntilSession: number;
      timezone: string;
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'notifications',
      template: EMAIL_TEMPLATES.SESSION_REMINDER,
      to: recipientEmail,
      bcc: reminderData.tutorEmail,
      subject: `Session Reminder: ${reminderData.subject} with ${reminderData.tutorName} in ${reminderData.minutesUntilSession} minutes`,
      data: {
        student_name: reminderData.studentName,
        tutor_name: reminderData.tutorName,
        tutor_email: reminderData.tutorEmail,
        session_date: reminderData.sessionDate,
        session_time: reminderData.sessionTime,
        subject: reminderData.subject,
        meeting_url: reminderData.meetingUrl,
        session_type: reminderData.sessionType,
        minutes_until_session: reminderData.minutesUntilSession,
        timezone: reminderData.timezone,
        support_email: 'support@rflearn.com',
        company_name: 'rfLearn'
      }
    });
  }

  /**
   * Send welcome email to new tutors
   */
  async sendWelcomeTutor(
    recipientEmail: string,
    tutorData: {
      firstName: string;
      lastName: string;
      dashboardUrl: string;
      profileSetupUrl: string;
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'onboarding',
      template: EMAIL_TEMPLATES.WELCOME_TUTOR,
      to: recipientEmail,
      subject: `Welcome to rfLearn, ${tutorData.firstName}! Start your tutoring journey`,
      data: {
        first_name: tutorData.firstName,
        last_name: tutorData.lastName,
        dashboard_url: tutorData.dashboardUrl,
        profile_setup_url: tutorData.profileSetupUrl,
        support_email: 'support@rflearn.com',
        company_name: 'rfLearn'
      }
    });
  }

  /**
   * Send payment confirmation email
   */
  async sendPaymentConfirmation(
    recipientEmail: string,
    paymentData: {
      studentName: string;
      amount: string;
      currency: string;
      paymentMethod: string;
      transactionId: string;
      sessionDetails: {
        tutorName: string;
        subject: string;
        date: string;
        time: string;
      };
      receiptUrl?: string;
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'notifications',
      template: EMAIL_TEMPLATES.PAYMENT_CONFIRMATION,
      to: recipientEmail,
      subject: `Payment Confirmation - ${paymentData.amount} ${paymentData.currency}`,
      data: {
        student_name: paymentData.studentName,
        amount: paymentData.amount,
        currency: paymentData.currency,
        payment_method: paymentData.paymentMethod,
        transaction_id: paymentData.transactionId,
        tutor_name: paymentData.sessionDetails.tutorName,
        subject: paymentData.sessionDetails.subject,
        session_date: paymentData.sessionDetails.date,
        session_time: paymentData.sessionDetails.time,
        receipt_url: paymentData.receiptUrl,
        support_email: 'support@rflearn.com',
        company_name: 'rfLearn'
      }
    });
  }

  /**
   * Send demo cancellation email
   */
  async sendDemoCancellation(
    recipientEmail: string,
    cancellationData: {
      studentName: string;
      tutorName: string;
      tutorEmail: string;
      originalDate: string;
      originalTime: string;
      subject: string;
      reason?: string;
      rescheduleUrl?: string;
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'booking',
      template: EMAIL_TEMPLATES.DEMO_CANCELLATION,
      to: recipientEmail,
      bcc: cancellationData.tutorEmail,
      subject: `Demo Session Cancelled - ${cancellationData.subject} with ${cancellationData.tutorName}`,
      data: {
        student_name: cancellationData.studentName,
        tutor_name: cancellationData.tutorName,
        tutor_email: cancellationData.tutorEmail,
        original_date: cancellationData.originalDate,
        original_time: cancellationData.originalTime,
        subject: cancellationData.subject,
        cancellation_reason: cancellationData.reason,
        reschedule_url: cancellationData.rescheduleUrl,
        support_email: 'support@rflearn.com',
        company_name: 'rfLearn'
      }
    });
  }

  /**
   * Send email verification
   */
  async sendEmailVerification(
    recipientEmail: string,
    verificationData: {
      firstName: string;
      verificationUrl: string;
      userType: 'student' | 'tutor';
    }
  ): Promise<EmailResponse> {
    return this.sendTemplateEmail({
      channel: 'onboarding',
      template: EMAIL_TEMPLATES.EMAIL_VERIFICATION,
      to: recipientEmail,
      subject: `Verify your email address - rfLearn`,
      data: {
        first_name: verificationData.firstName,
        verification_url: verificationData.verificationUrl,
        user_type: verificationData.userType,
        support_email: 'support@rflearn.com',
        company_name: 'rfLearn'
      }
    });
  }

  /**
   * Send custom email with raw HTML (for one-off emails)
   */
  async sendCustomEmail(
    recipientEmail: string,
    subject: string,
    htmlContent: string,
    textContent?: string
  ): Promise<EmailResponse> {
    return this.sendRawEmail({
      channel: 'notifications',
      to: recipientEmail,
      subject,
      rawHtml: htmlContent,
      text: textContent
    });
  }
}

export const emailService = new EmailService();
